import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { FaBuilding, FaPlus, FaSearch, FaSitemap } from "react-icons/fa";
import AdLayout from "../components/AdLayout";
import DepartmentEditor from "../components/admin/DepartmentEditor";
import "../css/DepartmentDetail.css";
import "../css/DepartmentModule.css";
import "../css/EnterprisePermissionPage.css";
import DivisionPermissionEditor from "./DivisionPermissionEditor";
import axiosInstance from "../axiosInstance";
import { fetchDepartmentMenuPermissions } from "../libs/customerCreatePermission";
import { formatDivisionLabelWithUserCount, getDivisionUserCount } from "./departmentFeatures";
import { toast } from "react-toastify";

function findParentDeptIdForDivision(items, divisionId) {
  for (const dep of items || []) {
    if (dep.divisions?.some((d) => Number(d.id) === Number(divisionId))) return Number(dep.id);
  }
  return null;
}

function findDivision(items, divisionId) {
  for (const dep of items || []) {
    const div = dep.divisions?.find((d) => Number(d.id) === Number(divisionId));
    if (div) return div;
  }
  return null;
}

function pickInitialDivision(items) {
  for (const dep of items || []) {
    const first = dep.divisions?.[0];
    if (first) return { deptId: Number(dep.id), divisionId: Number(first.id) };
  }
  return { deptId: null, divisionId: null };
}

/** Chuẩn hóa để so khớp tìm kiếm: không phân biệt hoa thường, bỏ dấu (gõ không dấu vẫn khớp). */
function normalizeSearchFold(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\u0111/g, "d"); // đ → d (không tách bằng NFD)
}

/**
 * Phân quyền: cây phòng ban — bộ phận bên trái; bảng chức năng + users bên phải (giống chi tiết bộ phận).
 */
export default function EnterprisePermissionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editorRef = useRef(null);
  const [items, setItems] = useState([]);
  const [panelDivisionId, setPanelDivisionId] = useState(null);
  /** Phòng ban đang focus trên cây (chọn dòng phòng ban hoặc phòng ban chứa bộ phận đã chọn). */
  const [treeFocusDeptId, setTreeFocusDeptId] = useState(null);
  /** Giao diện highlight: 'dept' | 'division'. */
  const [treeSelection, setTreeSelection] = useState({ kind: "division", deptId: null, divisionId: null });
  const [deptFeaturePerm, setDeptFeaturePerm] = useState({
    canView: true,
    canCreate: true,
    canEdit: true,
    canDelete: true,
  });
  const [showDepartmentEditor, setShowDepartmentEditor] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [treeSearchQuery, setTreeSearchQuery] = useState("");

  const reloadDepartments = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/users/departments");
      const list = response.data || [];
      setItems(list);
      return list;
    } catch {
      setItems([]);
      return [];
    }
  }, []);

  useEffect(() => {
    reloadDepartments();
  }, [reloadDepartments]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchDepartmentMenuPermissions(axiosInstance);
      if (!cancelled) {
        setDeptFeaturePerm({
          canView: Boolean(p?.canView),
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const requestedDivisionIdFromUrl = useMemo(() => {
    const raw = searchParams.get("divisionId");
    if (raw == null || raw === "") return NaN;
    const n = Number(raw);
    return Number.isNaN(n) ? NaN : n;
  }, [searchParams]);

  const setDivisionIdInUrl = useCallback(
    (divisionId, { replace = false } = {}) => {
      const next = new URLSearchParams(searchParams);
      if (divisionId == null || divisionId === "") {
        next.delete("divisionId");
      } else {
        next.set("divisionId", String(divisionId));
      }
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams]
  );

  /** Đồng bộ cây + panel từ ?divisionId= (F5, back/forward, link trực tiếp). */
  useEffect(() => {
    if (!items?.length) return;

    if (!Number.isNaN(requestedDivisionIdFromUrl)) {
      const parentDept = findParentDeptIdForDivision(items, requestedDivisionIdFromUrl);
      if (parentDept != null) {
        setTreeFocusDeptId(parentDept);
        setPanelDivisionId(requestedDivisionIdFromUrl);
        setTreeSelection({
          kind: "division",
          deptId: parentDept,
          divisionId: requestedDivisionIdFromUrl,
        });
      }
      return;
    }

    const init = pickInitialDivision(items);
    if (init.divisionId != null) {
      setTreeFocusDeptId(init.deptId);
      setPanelDivisionId(init.divisionId);
      setTreeSelection({
        kind: "division",
        deptId: init.deptId,
        divisionId: init.divisionId,
      });
      setDivisionIdInUrl(init.divisionId, { replace: true });
    }
  }, [items, requestedDivisionIdFromUrl, setDivisionIdInUrl]);

  /**
   * Bộ phận đang xem trên panel: ưu tiên lựa chọn explicit (click cây), không thì URL (?divisionId=),
   * không thì bộ phận đầu tiên. Dùng useMemo để lần render đầu sau khi có `items` đã khớp URL,
   * tránh race với effect con (DivisionPermissionEditor) ghi đè `panelDivisionId` trước khi URL được áp.
   */
  const effectivePanelDivisionId = useMemo(() => {
    if (panelDivisionId != null && !Number.isNaN(Number(panelDivisionId))) {
      return Number(panelDivisionId);
    }
    if (!items?.length) return null;
    if (!Number.isNaN(requestedDivisionIdFromUrl)) {
      const parentDept = findParentDeptIdForDivision(items, requestedDivisionIdFromUrl);
      if (parentDept != null) return Number(requestedDivisionIdFromUrl);
    }
    const init = pickInitialDivision(items);
    return init.divisionId != null ? Number(init.divisionId) : null;
  }, [items, panelDivisionId, requestedDivisionIdFromUrl]);

  /** Phòng ban đang hiển thị / làm ngữ cảnh panel (kể cả khi chưa chọn bộ phận). */
  const panelDepartmentId = useMemo(() => {
    if (effectivePanelDivisionId != null) {
      const pid = findParentDeptIdForDivision(items, effectivePanelDivisionId);
      if (pid != null) return pid;
    }
    if (treeFocusDeptId != null) return Number(treeFocusDeptId);
    const firstWithDiv = items.find((d) => (d.divisions || []).length > 0);
    if (firstWithDiv) return Number(firstWithDiv.id);
    return items[0] ? Number(items[0].id) : null;
  }, [items, effectivePanelDivisionId, treeFocusDeptId]);

  const createDivisionDepartmentId = useMemo(() => {
    if (treeFocusDeptId != null) return Number(treeFocusDeptId);
    if (effectivePanelDivisionId != null) {
      const p = findParentDeptIdForDivision(items, effectivePanelDivisionId);
      if (p != null) return p;
    }
    return panelDepartmentId != null ? Number(panelDepartmentId) : undefined;
  }, [treeFocusDeptId, effectivePanelDivisionId, items, panelDepartmentId]);

  const panelHeaderTitle = useMemo(() => {
    if (effectivePanelDivisionId == null) return "—";
    return formatDivisionLabelWithUserCount(findDivision(items, effectivePanelDivisionId));
  }, [items, effectivePanelDivisionId]);

  /** Cây trái sau khi lọc theo tên phòng ban / bộ phận */
  const filteredEnterpriseTree = useMemo(() => {
    const q = normalizeSearchFold(treeSearchQuery);
    if (!q) {
      return items.map((dep) => ({
        dep,
        divisions: dep.divisions || [],
      }));
    }
    return items
      .map((dep) => {
        const divisions = dep.divisions || [];
        const deptMatch = normalizeSearchFold(dep.name).includes(q);
        const matchingDivs = divisions.filter((div) => normalizeSearchFold(div.name).includes(q));
        if (deptMatch) return { dep, divisions };
        if (matchingDivs.length) return { dep, divisions: matchingDivs };
        return null;
      })
      .filter(Boolean);
  }, [items, treeSearchQuery]);

  const onClickDept = (deptId) => {
    const id = Number(deptId);
    setTreeFocusDeptId(id);
    setTreeSelection({ kind: "dept", deptId: id, divisionId: null });
  };

  const onClickDivision = (deptId, divisionId) => {
    const dId = Number(deptId);
    const vId = Number(divisionId);
    setTreeFocusDeptId(dId);
    setPanelDivisionId(vId);
    setTreeSelection({ kind: "division", deptId: dId, divisionId: vId });
    setDivisionIdInUrl(vId, { replace: false });
  };

  const handlePanelDivisionChange = useCallback(
    (divisionId) => {
      const vId =
        divisionId == null || divisionId === "" ? null : Number(divisionId);
      setPanelDivisionId(vId);
      if (vId != null && !Number.isNaN(vId)) {
        const parentDept = findParentDeptIdForDivision(items, vId);
        if (parentDept != null) {
          setTreeFocusDeptId(parentDept);
          setTreeSelection({ kind: "division", deptId: parentDept, divisionId: vId });
        }
        setDivisionIdInUrl(vId, { replace: true });
      }
    },
    [items, setDivisionIdInUrl]
  );

  const openCreateDepartment = () => {
    if (!deptFeaturePerm.canCreate) return;
    setEditingDepartment(null);
    setShowDepartmentEditor(true);
  };

  const openCreateDivision = () => {
    if (!deptFeaturePerm.canEdit) return;
    if (createDivisionDepartmentId == null) return;
    editorRef.current?.openCreateDivision?.();
  };

  const openEditDivision = (division) => {
    if (!deptFeaturePerm.canEdit) return;
    editorRef.current?.openEditDivision?.(division);
  };

  const openEditDepartment = (dep) => {
    if (!(deptFeaturePerm.canView || deptFeaturePerm.canEdit)) return;
    setEditingDepartment(dep);
    setShowDepartmentEditor(true);
  };

  const handleSaveDepartment = async (model) => {
    try {
      if (model.id) {
        await axiosInstance.put(`/api/users/departments/${model.id}`, model);
      } else {
        await axiosInstance.post("/api/users/departments", model);
      }
      await reloadDepartments();
      setShowDepartmentEditor(false);
      setEditingDepartment(null);
    } catch (error) {
      const message = error?.response?.data?.message || "Không thể lưu phòng ban.";
      toast.error(message);
    }
  };

  const resolvedTreeDivisionId = useMemo(() => {
    if (treeSelection.kind === "dept") return null;
    if (treeSelection.kind === "division" && treeSelection.divisionId != null) {
      return Number(treeSelection.divisionId);
    }
    return effectivePanelDivisionId != null ? Number(effectivePanelDivisionId) : null;
  }, [treeSelection.kind, treeSelection.divisionId, effectivePanelDivisionId]);

  const deptRowClass = (depId) => {
    const id = Number(depId);
    const strong = treeSelection.kind === "dept" && treeSelection.deptId === id;
    const parentOfResolved =
      resolvedTreeDivisionId != null
        ? findParentDeptIdForDivision(items, resolvedTreeDivisionId)
        : null;
    const soft = resolvedTreeDivisionId != null && parentOfResolved === id;
    if (strong) return "enterprise-tree__row enterprise-tree__row--strong";
    if (soft) return "enterprise-tree__row enterprise-tree__row--soft";
    return "enterprise-tree__row";
  };

  const divRowClass = (depId, divisionId) => {
    const active =
      resolvedTreeDivisionId != null && resolvedTreeDivisionId === Number(divisionId);
    return active
      ? "enterprise-tree__row enterprise-tree__row--strong enterprise-tree__row--child"
      : "enterprise-tree__row enterprise-tree__row--child";
  };

  return (
    <AdLayout>
      {/* container-fluid p-2, row g-3, col-lg-3 | col-lg-9 */}
      <div className="container-fluid p-2 department-module">
        <div className="row g-3">
          <div className="col-lg-3">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span className="fw-semibold">Cấu trúc doanh nghiệp</span>
                {(deptFeaturePerm.canCreate || deptFeaturePerm.canEdit) && (
                  <Dropdown align="end">
                    <Dropdown.Toggle
                      size="sm"
                      variant="outline-secondary"
                      className="d-inline-flex align-items-center gap-1"
                      id="enterprise-tree-add-dropdown"
                    >
                      <FaPlus size={12} />
                      Thêm mới
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={openCreateDepartment} disabled={!deptFeaturePerm.canCreate}>
                        Thêm phòng ban
                      </Dropdown.Item>
                      <Dropdown.Item onClick={openCreateDivision} disabled={!deptFeaturePerm.canEdit || createDivisionDepartmentId == null}>
                        Thêm bộ phận
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
              <div className="enterprise-tree-search px-2 py-2 border-bottom bg-light">
                <div className="input-group input-group-sm">
                  <input
                    type="search"
                    className="form-control border-end-0"
                    placeholder="Tìm kiếm phòng ban, bộ phận..."
                    value={treeSearchQuery}
                    onChange={(e) => setTreeSearchQuery(e.target.value)}
                    aria-label="Tìm kiếm trong cấu trúc doanh nghiệp"
                  />
                  <span className="input-group-text py-0 bg-white border-start-0">
                    <FaSearch size={12} className="text-muted" aria-hidden />
                  </span>
                </div>
              </div>
              <div className="card-body p-2 enterprise-tree">
                {filteredEnterpriseTree.map(({ dep, divisions }) => (
                  <div key={dep.id} className="enterprise-tree__group mb-1">
                    <button
                      type="button"
                      className={deptRowClass(dep.id)}
                      onClick={() => onClickDept(dep.id)}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openEditDepartment(dep);
                      }}
                    >
                      <span className="enterprise-tree__row-main">
                        <FaBuilding className="enterprise-tree__icon-dept" aria-hidden />
                        <span className="enterprise-tree__label">{dep.name || "—"}</span>
                      </span>
                    </button>
                    <div className="enterprise-tree__children">
                      {divisions.map((div) => (
                        <button
                          key={div.id}
                          type="button"
                          className={divRowClass(dep.id, div.id)}
                          onClick={() => onClickDivision(dep.id, div.id)}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            openEditDivision(div);
                          }}
                        >
                          <span className="enterprise-tree__row-main">
                            <FaSitemap className="enterprise-tree__icon-division" aria-hidden />
                            <span
                              className="enterprise-tree__label enterprise-tree__label--with-count"
                              title={`${div.name || "—"} — ${getDivisionUserCount(div)} user`}
                            >
                              <span className="enterprise-tree__label-name">{div.name || "—"}</span>
                              <span className="enterprise-tree__label-count text-muted small">
                                ({getDivisionUserCount(div)})
                              </span>
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {!items.length && <div className="text-muted">Chưa có phòng ban.</div>}
                {items.length > 0 && filteredEnterpriseTree.length === 0 && (
                  <div className="text-muted small">Không có kết quả phù hợp.</div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-9 d-flex flex-column min-h-0">
            <div className="flex-grow-1 min-h-0 d-flex flex-column">
              {panelDepartmentId != null ? (
                <DivisionPermissionEditor
                  ref={editorRef}
                  departmentId={panelDepartmentId}
                  items={items}
                  setItems={setItems}
                  reloadDepartments={reloadDepartments}
                  selectedDivisionId={effectivePanelDivisionId}
                  setSelectedDivisionId={handlePanelDivisionChange}
                  requestedDivisionId={requestedDivisionIdFromUrl}
                  panelHeaderTitle={panelHeaderTitle}
                  createDivisionDepartmentId={createDivisionDepartmentId}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center flex-grow-1 text-muted p-4">
                  Chưa có dữ liệu bộ phận để hiển thị.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <DepartmentEditor
        show={showDepartmentEditor}
        model={editingDepartment}
        canCreate={deptFeaturePerm.canCreate}
        canEdit={deptFeaturePerm.canEdit}
        canDelete={deptFeaturePerm.canDelete}
        onDeleted={reloadDepartments}
        onClose={() => {
          setShowDepartmentEditor(false);
          setEditingDepartment(null);
        }}
        onSave={handleSaveDepartment}
      />
    </AdLayout>
  );
}
